import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getColor, setTheme, toggleTheme, getAvailableThemes } from '../utils/colorPalette';
import { THEMES, DEFAULT_THEME, THEME_STORAGE_KEY, THEME_METADATA } from '../utils/themeConstants';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(DEFAULT_THEME);
  const [isLoading, setIsLoading] = useState(true);
  const user = useSelector((state) => state.user.userInfo);

  // Initialize theme from localStorage or user settings
  useEffect(() => {
    const initializeTheme = () => {
      try {
        // Priority: user theme preference > localStorage > default
        let theme = DEFAULT_THEME;

        if (user?.theme && user.theme !== 'system' && getAvailableThemes().includes(user.theme)) {
          theme = user.theme;
        } else {
          const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
          if (savedTheme && getAvailableThemes().includes(savedTheme)) {
            theme = savedTheme;
          }
        }

        setTheme(theme);
        setCurrentTheme(theme);
        document.body.setAttribute('data-theme', theme);
        setIsLoading(false);
      } catch (error) {
        console.error('Error initializing theme:', error);
        setTheme(DEFAULT_THEME);
        setCurrentTheme(DEFAULT_THEME);
        setIsLoading(false);
      }
    };

    initializeTheme();
  }, [user?.theme]);

  // Sync theme when user info changes (e.g., after login or settings update)
  // Only sync when user.theme changes, not when currentTheme changes (to avoid overriding manual toggles)
  useEffect(() => {
    if (user?.theme && user.theme !== 'system' && getAvailableThemes().includes(user.theme)) {
      // Only sync if localStorage doesn't match user.theme (meaning it's a server-side change)
      // If localStorage matches currentTheme, it means the user just toggled locally
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      if (user.theme !== currentTheme && user.theme !== savedTheme) {
        setTheme(user.theme);
        setCurrentTheme(user.theme);
        localStorage.setItem(THEME_STORAGE_KEY, user.theme);
        document.body.setAttribute('data-theme', user.theme);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.theme]);

  const changeTheme = useCallback((themeParam) => {
    let resolvedTheme = themeParam;
    if (themeParam === 'system') {
      // Handle system theme (could detect OS preference)
      const prefersDark =
        window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      resolvedTheme = prefersDark ? THEMES.DARK : THEMES.LIGHT;
    }

    if (!getAvailableThemes().includes(resolvedTheme)) {
      console.warn(`Theme "${resolvedTheme}" is not available`);
      return;
    }

    try {
      setTheme(resolvedTheme);
      setCurrentTheme(resolvedTheme);
      localStorage.setItem(THEME_STORAGE_KEY, resolvedTheme);
      document.body.setAttribute('data-theme', resolvedTheme);

      // The Settings component will handle the API call
      // We don't do it here to avoid circular dependencies
    } catch (error) {
      console.error('Error changing theme:', error);
    }
  }, []);

  const toggleCurrentTheme = useCallback(() => {
    const newTheme = toggleTheme();
    setCurrentTheme(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    document.body.setAttribute('data-theme', newTheme);
    return newTheme;
  }, []);

  const getThemeColor = useCallback(
    (colorName, shade = 50) => {
      return getColor(colorName, shade, currentTheme);
    },
    [currentTheme]
  );

  const getThemeMetadata = useCallback(() => {
    return THEME_METADATA[currentTheme] || THEME_METADATA[DEFAULT_THEME];
  }, [currentTheme]);

  const getAvailableThemesList = useCallback(() => {
    return getAvailableThemes().map((theme) => ({
      value: theme,
      ...THEME_METADATA[theme],
    }));
  }, []);

  const value = useMemo(
    () => ({
      currentTheme,
      isLoading,
      changeTheme,
      toggleCurrentTheme,
      getThemeColor,
      getThemeMetadata,
      getAvailableThemesList,
      isDark: currentTheme === THEMES.DARK,
      isLight: currentTheme === THEMES.LIGHT,
    }),
    [
      currentTheme,
      isLoading,
      changeTheme,
      toggleCurrentTheme,
      getThemeColor,
      getThemeMetadata,
      getAvailableThemesList,
    ]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
